/**
 * Custom secure session management for client API calls.
 * Implements access token (Bearer) automatic injection, 
 * proactive token expiration check, and rotatable refresh token exchange.
 */
import { auth } from "../services/firebase";
import { safeClone } from "./utils";

interface SessionState {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
  userId: string;
  email: string;
}

export async function safeJsonResponse<T = any>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text || text.trim() === "" || text.trim() === "undefined" || text.trim() === "null") {
    return {} as T;
  }
  if (text.startsWith("<!DOCTYPE") || text.startsWith("<html")) {
    throw new Error(`Server API unavailable or returned HTML (status ${res.status}). Ensure backend server is running.`);
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    return {} as T;
  }
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: "main_nutrition_access_token",
  REFRESH_TOKEN: "main_nutrition_refresh_token",
  ACCESS_EXPIRES_AT: "main_nutrition_access_expires_at",
  USER_ID: "main_nutrition_session_user_id",
  EMAIL: "main_nutrition_session_email"
};

/**
 * Detects if a JWT is a direct Google Firebase ID token (RS256 issued by securetoken.google.com)
 * rather than a server-signed application session token (HS256).
 */
export function isFirebaseIdToken(token: string | null | undefined): boolean {
  if (!token || typeof token !== "string" || !token.includes(".")) return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;

    // Decode header
    const base64UrlHeader = parts[0].replace(/-/g, "+").replace(/_/g, "/");
    const jsonHeader = atob(base64UrlHeader);
    const header = JSON.parse(jsonHeader);
    if (header.alg === "RS256") return true;

    // Decode payload
    const base64UrlPayload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = atob(base64UrlPayload);
    const payload = JSON.parse(jsonPayload);
    if (typeof payload.iss === "string" && payload.iss.includes("securetoken.google.com")) return true;
    if (payload.firebase !== undefined || payload.auth_time !== undefined) return true;
  } catch (e) {
    // If not parseable as base64/JSON, return false
  }
  return false;
}

export function getSession(): SessionState | null {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const expiresAtStr = localStorage.getItem(STORAGE_KEYS.ACCESS_EXPIRES_AT);
  const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  const email = localStorage.getItem(STORAGE_KEYS.EMAIL) || "";

  if (!accessToken || !refreshToken || !expiresAtStr || !userId) {
    return null;
  }

  // Self-healing check: purge any raw Firebase ID token that was erroneously saved as an access token
  if (isFirebaseIdToken(accessToken)) {
    console.warn("Client: Stored access token is a raw Firebase ID token. Purging poisoned session.");
    clearSession();
    return null;
  }

  return {
    accessToken,
    refreshToken,
    accessTokenExpiresAt: Number(expiresAtStr),
    userId,
    email
  };
}

export function saveSession(session: Omit<SessionState, "accessTokenExpiresAt"> & { accessTokenExpiresAt?: number }) {
  if (isFirebaseIdToken(session.accessToken)) {
    console.error("Client: Refusing to save raw Firebase ID token as application accessToken!");
    return;
  }
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, session.accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, session.refreshToken);
  
  // Default to 30 days from now if not explicitly passed
  const expiresAt = session.accessTokenExpiresAt || (Date.now() + 30 * 24 * 60 * 60 * 1000);
  localStorage.setItem(STORAGE_KEYS.ACCESS_EXPIRES_AT, String(expiresAt));
  localStorage.setItem(STORAGE_KEYS.USER_ID, session.userId);
  localStorage.setItem(STORAGE_KEYS.EMAIL, session.email);
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ACCESS_EXPIRES_AT);
  localStorage.removeItem(STORAGE_KEYS.USER_ID);
  localStorage.removeItem(STORAGE_KEYS.EMAIL);
}

// Global flag to prevent concurrent refresh requests
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

/**
 * Perform a silent rotation of the refresh token and obtain a new access/refresh pair
 */
async function performTokenRefreshRaw(): Promise<string> {
  const current = getSession();
  if (!current || !current.refreshToken || isFirebaseIdToken(current.refreshToken)) {
    throw new Error("No valid refresh token available");
  }

  console.log("Client: Initiating rotatable refresh token flow...");
  const res = await fetch("/api/auth/session/refresh", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(safeClone({ refreshToken: current.refreshToken }))
  });

  if (!res.ok) {
    throw new Error("Session expired or revoked on server");
  }

  const data = await safeJsonResponse(res);
  if (!data?.accessToken || isFirebaseIdToken(data.accessToken)) {
    throw new Error("Server returned invalid access token");
  }

  saveSession({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken || current.refreshToken,
    accessTokenExpiresAt: data.accessTokenExpiresAt,
    userId: current.userId,
    email: current.email
  });

  console.log("Client: Token rotation completed successfully.");
  return data.accessToken;
}

/**
 * Obtains a lightweight guest session token so unauthenticated/guest users
 * can interact with AI chat and public APIs without session expiration barriers.
 */
export async function asyncGetGuestSession(): Promise<string> {
  try {
    const res = await fetch("/api/auth/session/guest", {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    if (res.ok) {
      const data = await safeJsonResponse(res);
      if (data?.accessToken) {
        saveSession({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpiresAt: data.accessTokenExpiresAt,
          userId: data.userId,
          email: data.email || ""
        });
        console.log("Client: Obtained guest session token successfully.");
        return data.accessToken;
      }
    }
  } catch (err) {
    console.warn("Client: Failed to obtain guest session token:", err);
  }
  return "";
}

export async function fetchWrapper(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Determine the absolute URL or pathname
  let inputStr = "";
  if (typeof input === "string") {
    inputStr = input;
  } else if (input instanceof URL) {
    inputStr = input.toString();
  } else if (input && typeof input === "object" && "url" in input) {
    inputStr = (input as any).url || "";
  }
  
  let pathname = inputStr;
  if (inputStr.startsWith("http://") || inputStr.startsWith("https://")) {
    try {
      const urlObj = new URL(inputStr);
      if (urlObj.origin === window?.location?.origin) {
        pathname = urlObj.pathname;
      }
    } catch (e) {
      // safe fallback
    }
  } else if (inputStr.startsWith("/")) {
    pathname = inputStr;
  }

  const isAuthRequest = pathname.includes("/api/auth/");
  const isApiRequest = !isAuthRequest && (
                       pathname.startsWith("/api/") || 
                       pathname.startsWith("/gemini-api-proxy/") ||
                       pathname.startsWith("/v1beta/") ||
                       pathname.startsWith("/v1/")
  );

  if (isApiRequest) {
    try {
      const token = await asyncGetAccessToken();
      if (token) {
        // Prepare headers
        const headers = new Headers(init?.headers);
        headers.set("Authorization", `Bearer ${token}`);
        
        // Merge into init
        init = {
          ...init,
          headers
        };

        // If input is a Request, mutate its headers or re-create it to apply authorization
        if (input && typeof input === "object" && (input instanceof Request || "headers" in input)) {
          try {
            (input as any).headers.set("Authorization", `Bearer ${token}`);
          } catch (e) {
            // If headers are immutable, we recreate the Request object
            try {
              input = new Request(input as Request, { headers });
            } catch (err) {
              console.warn("Failed to recreate Request with auth headers:", err);
            }
          }
        }
      }
    } catch (tokenError) {
      console.warn("API Interceptor: failed to retrieve access token, proceeding without auth:", tokenError);
    }
  }

  let response = await fetch(input, init);
  
  if (isApiRequest && response.status === 401) {
    console.warn("API Interceptor: Received 401 Unauthorized. Forcing token refresh and retrying...");
    try {
      clearSession();
      // Force refresh using the full recovery logic
      let newToken = "";
      if (auth?.currentUser) {
        newToken = await forceRecreateSessionWithFirebase(true);
      }
      if (!newToken) {
        newToken = await asyncGetGuestSession();
      }
      if (!newToken) {
        newToken = await asyncGetAccessToken(true);
      }
      
      if (newToken && !isFirebaseIdToken(newToken)) {
        const headers = new Headers(init?.headers);
        headers.set("Authorization", `Bearer ${newToken}`);
        init = { ...init, headers };
        
        let retryInput: RequestInfo | URL = input;
        if (input && typeof input === "object" && (input instanceof Request || "headers" in input)) {
          try {
            (input as any).headers.set("Authorization", `Bearer ${newToken}`);
            retryInput = input;
          } catch (e) {
            try {
              retryInput = new Request(input as Request, { headers });
            } catch (err) {
              retryInput = input;
            }
          }
        }

        // Retry the request
        response = await fetch(retryInput, init);
      }
    } catch (refreshErr) {
      console.error("API Interceptor: Retry after 401 failed:", refreshErr);
      // Let it fall through and return the 401 response
    }
  }
  
  return response;
}

/**
 * Forces re-creation of a secure server session directly using the user's active Google Firebase ID token.
 * Exchanges the Firebase ID token for a server-signed HMAC session token via /api/auth/session/create.
 */
export async function forceRecreateSessionWithFirebase(forceRefresh: boolean = true): Promise<string> {
  const currentUser = auth?.currentUser;
  if (!currentUser) return "";
  let idToken = "";
  try {
    idToken = await Promise.race([
      currentUser.getIdToken(forceRefresh),
      new Promise<string>((_, reject) => setTimeout(() => reject(new Error('ID_TOKEN_TIMEOUT')), 10000))
    ]);
  } catch (idErr) {
    console.warn("Client: getIdToken failed, attempting cached token:", idErr);
    try {
      idToken = await currentUser.getIdToken(false);
    } catch (e) {
      return "";
    }
  }

  if (!idToken) return "";

  // Retry up to 2 times to handle Cloud Run cold starts (up to 20s each)
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);
      const sessionRes = await fetch("/api/auth/session/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(safeClone({
          idToken,
          email: currentUser.email || "",
          userId: currentUser.uid
        })),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (sessionRes.ok) {
        const data = await safeJsonResponse(sessionRes);
        if (data?.accessToken && !isFirebaseIdToken(data.accessToken)) {
          saveSession({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken || "",
            accessTokenExpiresAt: data.accessTokenExpiresAt || (Date.now() + 30 * 24 * 60 * 60 * 1000),
            userId: data.userId || currentUser.uid,
            email: data.email || currentUser.email || ""
          });
          console.log("Client: Server session successfully re-created via Firebase ID token exchange.");
          return data.accessToken;
        }
      } else {
        console.warn(`Client: /api/auth/session/create returned status ${sessionRes.status}`);
      }
    } catch (err: any) {
      console.warn(`Client: /api/auth/session/create attempt ${attempt + 1} notice:`, err?.message || err);
      if (attempt === 0) {
        await new Promise(r => setTimeout(r, 1000));
      }
    }
  }

  console.warn("Client: Failed to obtain server session token from /api/auth/session/create.");
  return "";
}

export async function asyncGetAccessToken(forceRefresh: boolean = false): Promise<string> {
  if (auth && typeof auth.authStateReady === 'function') {
    try {
      await Promise.race([
        auth.authStateReady(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('AUTH_STATE_TIMEOUT')), 4000))
      ]);
    } catch (e) {}
  }
  
  let session = getSession();
  const currentUser = auth?.currentUser;
  
  // 1. If Firebase user is authenticated, prioritize server session sync if session is missing, expired, or forced
  if (currentUser) {
    const isExpired = !session || Date.now() > (session.accessTokenExpiresAt - 60 * 1000);
    if (!session || isExpired || forceRefresh || session.userId !== currentUser.uid) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          console.log("Client: Syncing server session from authenticated Firebase user...");
          const token = await forceRecreateSessionWithFirebase(forceRefresh);
          isRefreshing = false;
          onRefreshed(token);
          if (token && !isFirebaseIdToken(token)) return token;
        } catch (e) {
          isRefreshing = false;
          onRefreshed("");
        }
      } else {
        const token = await Promise.race([
          new Promise<string>((resolve) => subscribeTokenRefresh((newToken) => resolve(newToken))),
          new Promise<string>((resolve) => setTimeout(() => resolve(""), 6000))
        ]);
        if (token && !isFirebaseIdToken(token)) return token;
      }
    }
  }

  // 2. If no session and no currentUser, obtain a guest session so AI chat and calls never get blocked
  if (!session && !currentUser) {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const guestToken = await asyncGetGuestSession();
        isRefreshing = false;
        onRefreshed(guestToken);
        if (guestToken && !isFirebaseIdToken(guestToken)) return guestToken;
      } catch (err) {
        isRefreshing = false;
        onRefreshed("");
        return "";
      }
    } else {
      const token = await Promise.race([
        new Promise<string>((resolve) => subscribeTokenRefresh((newToken) => resolve(newToken))),
        new Promise<string>((resolve) => setTimeout(() => resolve(""), 3000))
      ]);
      if (token && !isFirebaseIdToken(token)) return token;
    }
  }

  session = getSession();
  if (!session) {
    return "";
  }

  let token = session.accessToken;
  const isAboutToExpire = Date.now() > (session.accessTokenExpiresAt - 45 * 1000); // 45 second proactive refresh threshold
  
  if (isAboutToExpire || forceRefresh) {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        token = await performTokenRefreshRaw();
        isRefreshing = false;
        onRefreshed(token);
      } catch (e: any) {
        console.warn("Token refresh attempt in asyncGetAccessToken:", e?.message || e);
        
        const isNetworkError = e.message?.includes("Failed to fetch") || e.message?.includes("Network error") || e.name === 'TypeError';
        
        // 1. Try to recover using Firebase Auth if the user is still logged in
        const user = auth?.currentUser;
        if (user) {
          try {
            console.log("Client: Session refresh failed, recovering via Firebase ID token...");
            const recoveryToken = await forceRecreateSessionWithFirebase(true);
            if (recoveryToken && !isFirebaseIdToken(recoveryToken)) {
              isRefreshing = false;
              onRefreshed(recoveryToken);
              return recoveryToken;
            }
          } catch (recoverErr: any) {
            console.error("Client: Failed to recover session dynamically:", recoverErr);
          }
        }
        
        // 2. If network error, preserve session and return existing token only if not permanently expired
        const now = Date.now();
        const isStrictlyExpired = session?.accessTokenExpiresAt ? now > session.accessTokenExpiresAt : true;
        if (isNetworkError && session?.accessToken && !isStrictlyExpired && !isFirebaseIdToken(session.accessToken)) {
           console.warn("Client: Network error detected during token refresh. Preserving non-expired session.");
           isRefreshing = false;
           onRefreshed(session.accessToken);
           return session.accessToken;
        }
        
        // 3. Fallback to guest session instead of breaking user scanning or chat flow
        try {
          console.log("Client: Falling back to fresh guest session...");
          const guestToken = await asyncGetGuestSession();
          if (guestToken && !isFirebaseIdToken(guestToken)) {
            isRefreshing = false;
            onRefreshed(guestToken);
            return guestToken;
          }
        } catch (guestErr) {
          console.warn("Client: Guest session fallback failed:", guestErr);
        }

        isRefreshing = false;
        clearSession();
        onRefreshed("");
        return "";
      }
    } else {
      // Queue the request with a strict 4-second safety timeout so it never hangs
      token = await Promise.race([
        new Promise<string>((resolve) => subscribeTokenRefresh((newToken) => resolve(newToken))),
        new Promise<string>((resolve) => setTimeout(() => resolve(session?.accessToken || ""), 4000))
      ]);
    }
  }

  if (token && isFirebaseIdToken(token)) {
    clearSession();
    return "";
  }

  return token || session?.accessToken || "";
}

/**
 * Stripe Billing & Subscription API Clients
 */
export async function createStripeCheckoutSession(
  tier: 'pro' | 'premium',
  interval: 'month' | 'year' = 'month'
): Promise<{ url: string; sessionId?: string }> {
  const returnUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const response = await fetchWrapper("/api/stripe/create-checkout-session", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(safeClone({ tier, interval, returnUrl }))
  });

  const data = await safeJsonResponse(response);
  if (!response.ok || !data.url) {
    throw new Error(data.error || "Failed to initialize Stripe checkout session.");
  }

  return data;
}

export async function createStripePortalSession(): Promise<{ url: string }> {
  const response = await fetchWrapper("/api/stripe/create-portal-session", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    }
  });

  const data = await safeJsonResponse(response);
  if (!response.ok || !data.url) {
    throw new Error(data.error || "Failed to open Stripe billing portal.");
  }

  return data;
}

export async function getStripeSubscription(): Promise<{
  tier: string;
  status: string;
  expiresAt: number | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  hasActiveSubscription: boolean;
}> {
  const response = await fetchWrapper("/api/stripe/subscription", {
    method: "GET"
  });

  const data = await safeJsonResponse(response);
  if (!response.ok) {
    throw new Error(data.error || "Failed to fetch subscription status.");
  }

  return data;
}
